/**
 * SharePoint API client service
 */

import { apiService } from '../../../services/api';
import type {
  SharePointSite,
  SharePointLibrary,
  SharePointBrowseResult,
  SharePointItemDetails,
  SharePointItem,
  SharePointItemType
} from '../types/sharepoint';

const API_BASE_PATH = '/api/sharepoint';

class SharePointService {
  /**
   * Get list of available SharePoint sites
   */
  async getSites(): Promise<SharePointSite[]> {
    const response = await apiService.get(`${API_BASE_PATH}/sites`);
    return response;
  }

  /**
   * Get document libraries for a specific site
   */
  async getLibraries(siteKey: string): Promise<SharePointLibrary[]> {
    const response = await apiService.get(`${API_BASE_PATH}/sites/${siteKey}/libraries`);
    return response;
  }

  /**
   * Browse contents of a folder in a SharePoint document library
   */
  async browseFolder(
    siteKey: string,
    driveId: string,
    folderPath: string = '',
    itemType: SharePointItemType = 'all'
  ): Promise<SharePointBrowseResult> {
    const params = new URLSearchParams({
      folder_path: folderPath,
      item_type: itemType
    });
    const response = await apiService.get(
      `${API_BASE_PATH}/sites/${siteKey}/drives/${driveId}/browse?${params}`
    );
    return response;
  }

  /**
   * Get detailed information about a specific item
   */
  async getItemDetails(
    siteKey: string,
    driveId: string,
    itemId: string
  ): Promise<SharePointItemDetails> {
    const response = await apiService.get(
      `${API_BASE_PATH}/sites/${siteKey}/drives/${driveId}/items/${itemId}`
    );
    return response;
  }

  /**
   * Search for items in a document library
   */
  async searchItems(
    siteKey: string,
    driveId: string,
    query: string,
    itemType: SharePointItemType = 'all'
  ): Promise<SharePointItem[]> {
    const params = new URLSearchParams({
      query,
      item_type: itemType
    });
    const response = await apiService.get(
      `${API_BASE_PATH}/sites/${siteKey}/drives/${driveId}/search?${params}`
    );
    return response;
  }

  /**
   * Build full path for an item
   */
  buildItemPath(item: SharePointItem, currentPath: string): string {
    if (!currentPath) {
      return item.name;
    }
    return `${currentPath}/${item.name}`;
  }

  /**
   * Check if a file matches the type filter
   */
  matchesFileTypeFilter(item: SharePointItem, filter?: string[]): boolean {
    if (!filter || filter.length === 0) {
      return true;
    }
    if (item.type !== 'file') {
      return true; // Always show folders
    }
    const extension = item.name.substring(item.name.lastIndexOf('.')).toLowerCase();
    return filter.some(ext => ext.toLowerCase() === extension);
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  }

  /**
   * Get file icon based on mime type or extension
   */
  getFileIcon(item: SharePointItem): string {
    if (item.type === 'folder') {
      return 'folder';
    }

    const extension = item.name.substring(item.name.lastIndexOf('.')).toLowerCase();
    const mimeType = item.mimeType?.toLowerCase() || '';

    // Map common file types to Material-UI icons
    if (mimeType.includes('pdf') || extension === '.pdf') {
      return 'picture_as_pdf';
    }
    if (mimeType.includes('word') || ['.doc', '.docx'].includes(extension)) {
      return 'description';
    }
    if (mimeType.includes('excel') || ['.xls', '.xlsx'].includes(extension)) {
      return 'table_chart';
    }
    if (mimeType.includes('powerpoint') || ['.ppt', '.pptx'].includes(extension)) {
      return 'slideshow';
    }
    if (mimeType.includes('image') || ['.jpg', '.jpeg', '.png', '.gif', '.svg'].includes(extension)) {
      return 'image';
    }
    if (mimeType.includes('video') || ['.mp4', '.avi', '.mov'].includes(extension)) {
      return 'videocam';
    }
    if (mimeType.includes('audio') || ['.mp3', '.wav', '.m4a'].includes(extension)) {
      return 'audiotrack';
    }
    if (mimeType.includes('zip') || ['.zip', '.rar', '.7z', '.tar', '.gz'].includes(extension)) {
      return 'folder_zip';
    }
    if (mimeType.includes('text') || ['.txt', '.md', '.log'].includes(extension)) {
      return 'text_snippet';
    }
    if (['.js', '.ts', '.jsx', '.tsx', '.py', '.java', '.cs', '.cpp', '.c', '.h'].includes(extension)) {
      return 'code';
    }

    return 'insert_drive_file'; // Default file icon
  }
}

// Export singleton instance
export const sharePointService = new SharePointService();